import React, { useEffect, useState } from "react";

import axios from "axios";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBBtn,
} from "mdb-react-ui-kit";
import { isAfter } from "date-fns";
import CommitteeMembers from "./Members";
import CommitteeStaff from "./Staff";
import getCommitteesMeetings from "../../utils/getCommitteesMeetings";
import "../../scss/print.scss";

function Details({ id, type }) {
  const [committeeData, setCommitteeData] = useState(null);
  const [govdeliveryLink, setGovdeliveryLink] = useState("");
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const fetchCommitteeData = async () => {
      try {
        let apiUrl = "";
        if (type === "standing") {
          apiUrl = `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/standingCommittees/${id}`;
        } else if (type === "nonStanding") {
          apiUrl = `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/nonStandingCommittees/${id}`;
        }

        const response = await axios.get(apiUrl);
        setCommitteeData(response.data);
      } catch (error) {
        console.error("Error fetching committee data:", error);
      }
    };

    const fetchCommitteeMeetings = async () => {
      try {
        const meetings = await getCommitteesMeetings([id], type);

        const filteredUpcomingMeetings = meetings
          .filter((meeting) =>
            isAfter(new Date(meeting.meetingTime), new Date())
          )
          .map((meeting) => ({
            date: new Date(meeting.meetingTime).toLocaleString(),
            location: meeting.location || "No room specified",
          }));

        setUpcomingMeetings(filteredUpcomingMeetings);
      } catch (error) {
        console.error("Error fetching committee meetings:", error);

        setUpcomingMeetings([]);
      }
    };

    const fetchGovDelivery = async () => {
      try {
        const tryFetch = async (apiType) => {
          const apiUrl = `${process.env.REACT_APP_WP_HOST}/wp-json/legmt/v1/committees/search?committeesEntity=${apiType}&lawsId=${id}`;
          const response = await axios.get(apiUrl);
          return response.data;
        };

        let data;

        try {
          if(!(type === "StandingCommittee" || type === "NonStandingCommittee"))
            data = await tryFetch(type === "standing" ? "StandingCommittee" : "NonStandingCommittee");
          else
            data = await tryFetch(type);
        } catch (err) {
          console.warn(`GovDelivery fetch failed with type "${type}"`, err);
        }
    
        const govDelivery = data?.govDeliveryUrl || "";
        setGovdeliveryLink(govDelivery);
      } catch (error) {
        console.error("Error in fetchGovDelivery:", error);
        setGovdeliveryLink("");
      }
    };

    fetchCommitteeData();
    fetchCommitteeMeetings();
    fetchGovDelivery();
  }, [id, type]);

  if (!committeeData) return <p>Loading...</p>;

  const { committeeDetails, memberships, staffMemberships, defaultDays } =
    committeeData;
  // removing the expired staff and legs
  const today = new Date();
  const activeMemberships = memberships?.filter(
    (member) => !member.endDate || new Date(member.endDate) >= today
  );
  const activeStaffMemberships = staffMemberships?.filter(
    (staff) => !staff.endDate || new Date(staff.endDate) >= today
  );
  const { committeeCode, defaultRoom, defaultTime, comEmail, zoomEmail } =
    committeeDetails || {};
  // const { description } = committeeCode?.committeeType || {};

  return (
    <>
      <MDBContainer fluid>
        <MDBRow>
          <MDBCol xl="8" lg="9" className="order-2 order-lg-1">
            {/* <p>{description}</p> */}
            <CommitteeMembers
              members={activeMemberships}
              committeeCode={committeeData.committeeDetails.committeeCode.code}
            />
          </MDBCol>
          <MDBCol xl="4" lg="3" order="order-1 order-lg-2">
            {/* Upcoming Meetings */}
            <div className="text-center mb-3">
              <MDBBtn onClick={handlePrint}>Print Details</MDBBtn>
            </div>
            <MDBCard className="rounded-0 bg-custom3 mb-3">
              <MDBCardBody>
                <h5 className="sub-heading">Upcoming Meetings</h5>
                {upcomingMeetings.length > 0 ? (
                  <ul className="mb-0">
                    {upcomingMeetings
                      .sort((a, b) => new Date(a.date) - new Date(b.date)) // Sort by date
                      .map((meeting, index) => (
                        <li key={index} className="mb-2">
                          {new Date(meeting.date).toLocaleDateString("en-US", {
                            weekday: "long", // Add day of the week (e.g., Monday)
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true, // Use 12-hour format with AM/PM
                          })}
                          {meeting.location ? `, ${meeting.location}` : ""}
                        </li>
                      ))}
                  </ul>
                ) : committeeCode.code === "CWH" ||
                  committeeCode.code === "CWS" ? (
                  <div className="text-center">
                    <MDBBtn
                      href="https://www.legmt.gov/events/"
                      target="_blank"
                      rel="nofollow"
                      className="me-3 mt-3"
                    >
                      See Official Schedule
                    </MDBBtn>
                  </div>
                ) : (
                  <p className="mb-0">No upcoming meetings scheduled.</p>
                )}
                {committeeCode.code !== "CWH" &&
                  committeeCode.code !== "CWS" && (
                    <div className="mt-3 text-center">
                      <MDBBtn href="https://participate.legmt.gov/">
                        Public Participation
                      </MDBBtn>
                    </div>
                  )}
              </MDBCardBody>
            </MDBCard>
            {/* Staff */}
            <MDBCard className="mb-3 rounded-0">
              <MDBCardBody>
                {(defaultRoom || defaultTime || defaultDays) && (
                  <div className="mb-4">
                    <h5 className="sub-heading">Typical Schedule & Location</h5>
                    {defaultDays?.length > 0 && (
                      <p>
                        {defaultDays
                          ?.map((day) =>
                            day.dayOfWeek
                              .toLowerCase()
                              .replace(/^./, day.dayOfWeek[0].toUpperCase())
                          )
                          .join(", ")}
                        {defaultTime && <> @ {defaultTime}</>}
                      </p>
                    )}
                    {defaultTime && <p>{defaultRoom}</p>}
                  </div>
                )}
                <h5 className="sub-heading">Staff Members</h5>
                <CommitteeStaff
                  staffMembers={activeStaffMemberships}
                  committeeCode={committeeCode}
                />
                {committeeCode.code !== "CWH" &&
                  committeeCode.code !== "CWS" && (
                    <div className="d-flex justify-content-center">
                      <MDBBtn href="https://participate.legmt.gov/">
                        Contact Committee
                      </MDBBtn>
                      {govdeliveryLink && (
                        <MDBBtn
                          className="ms-2"
                          href={govdeliveryLink}
                          target="_blank"
                          rel="nofollow"
                        >
                          Notification Emails
                        </MDBBtn>
                      )}
                    </div>
                  )}
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </>
  );
}

export default Details;
